import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  CirclePause,
  CirclePlay,
  ClipboardList,
  Copy,
  Download,
  ExternalLink,
  HeartHandshake,
  ListTodo,
  RotateCcw,
} from "lucide-react";
import { browserTools } from "../toolCatalog";

const breathingPhases = [
  { name: "Breathe in", seconds: 4 },
  { name: "Hold gently", seconds: 4 },
  { name: "Breathe out", seconds: 6 },
];

const groundingSteps = [
  "Name five things you can see.",
  "Notice four things you can feel.",
  "Listen for three things you can hear.",
  "Find two things you can smell.",
  "Notice one thing you can taste—or take one slow breath.",
];

const tools = [
  { id: "breathe", label: "Breathing" },
  { id: "ground", label: "Grounding" },
  { id: "reflect", label: "Mood note" },
  { id: "prepare", label: "Visit prep" },
  { id: "support", label: "Support words" },
  { id: "sort", label: "Thought sorter" },
];

const visitTopics = [
  { id: "change", label: "What changed, when it began, and how often it happens" },
  { id: "daily", label: "Sleep, appetite, energy, concentration, or daily routines" },
  { id: "life", label: "Work, study, relationships, stressors, or recent life changes" },
  { id: "medicine", label: "Medicines, supplements, allergies, or past side effects" },
  { id: "history", label: "Previous support, treatment, and what did or did not help" },
  { id: "safety", label: "Any concern about safety, self-harm, or harming someone else" },
];

const supportScripts = [
  {
    id: "check-in",
    title: "Start a gentle check-in",
    note: "When you have noticed a change and want to open the door.",
    script: "I’ve noticed things have seemed harder lately, and I care about you. Would you like to talk, or would quiet company feel better right now?",
  },
  {
    id: "listen",
    title: "Listen without taking over",
    note: "When someone has started sharing and you are unsure what they need.",
    script: "Thank you for telling me. I’m here with you. Would you like me to listen, help think through options, or help you find support?",
  },
  {
    id: "professional",
    title: "Suggest professional support",
    note: "When the difficulty is continuing or affecting everyday life.",
    script: "You don’t have to work this out alone. Would you be open to speaking with a mental-health professional? I can help you look for support or go with you, if you want.",
  },
  {
    id: "urgent",
    title: "Respond to immediate danger",
    note: "When you believe someone may harm themselves or another person.",
    script: "I’m concerned about your immediate safety. I’m staying with you while we contact emergency support now.",
    urgent: true,
  },
];

export default function WellbeingTools({ initialTool = "", standalone = false }) {
  const queryTool = new URLSearchParams(window.location.search).get("tool");
  const requestedTool = initialTool || queryTool;
  const [activeTool, setActiveTool] = useState(tools.some((tool) => tool.id === requestedTool) ? requestedTool : "breathe");
  const tabRefs = useRef([]);
  const activeDefinition = browserTools.find((tool) => tool.id === activeTool) || browserTools[0];

  const selectTool = (id) => {
    setActiveTool(id);
    if (standalone) window.history.replaceState({}, "", `/tools/${id}`);
  };

  const moveTab = (event, index) => {
    const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    let next = index;
    if (event.key === "ArrowLeft") next = (index - 1 + tools.length) % tools.length;
    if (event.key === "ArrowRight") next = (index + 1) % tools.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = tools.length - 1;
    selectTool(tools[next].id);
    tabRefs.current[next]?.focus({ preventScroll: true });
  };

  return (
    <section className={`tools-section${standalone ? " tools-section--standalone" : ""}`} id="tools" aria-labelledby="tools-title">
      {standalone ? (
        <header className="tools-section__heading tools-section__heading--standalone">
          <div>
            <a className="page-back-link" href="/tools"><ChevronLeft size={17} aria-hidden="true" /> All tools</a>
            <p className="tool-kicker">Private browser tool</p>
            <h1 id="tools-title">{activeDefinition.title}</h1>
          </div>
          <p>{activeDefinition.description} No AI request is made, and your entries stay on this device.</p>
        </header>
      ) : (
        <div className="tools-section__heading">
          <div>
            <h2 id="tools-title">A small pause, or a clearer next step.</h2>
            <a href="/tools">Browse all tools <ChevronRight size={17} aria-hidden="true" /></a>
          </div>
          <p>I built these private, browser-based tools for moments when you want to settle, prepare, or find the words. Nothing here assesses, diagnoses, or treats a condition.</p>
        </div>
      )}

      {!standalone && (
        <figure className="tools-illustration">
          <img src="/images/illustrations/small-pause.jpg" alt="A tangled ink line finding a calm centre and continuing toward a warm doorway" width="1672" height="941" loading="lazy" />
          <figcaption>A pause does not have to solve everything. It can help you find one clearer next step.</figcaption>
        </figure>
      )}

      <div className="tools-tabs" role="tablist" aria-label="Wellbeing tools">
        {tools.map(({ id, label }, index) => (
          <button
            key={id}
            id={`tab-${id}`}
            ref={(element) => { tabRefs.current[index] = element; }}
            type="button"
            role="tab"
            aria-selected={activeTool === id}
            aria-controls={`tool-${id}`}
            tabIndex={activeTool === id ? 0 : -1}
            onClick={() => selectTool(id)}
            onKeyDown={(event) => moveTab(event, index)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tool-workspace">
        <BreathingTool hidden={activeTool !== "breathe"} />
        <GroundingTool hidden={activeTool !== "ground"} />
        <MoodTool hidden={activeTool !== "reflect"} />
        <VisitPrepTool hidden={activeTool !== "prepare"} />
        <SupportWordsTool hidden={activeTool !== "support"} />
        <ThoughtSorterTool hidden={activeTool !== "sort"} />
      </div>

      <p className="tool-sources">
        The visit and conversation prompts draw on public guidance from
        {" "}<a href="https://www.nimh.nih.gov/health/publications/tips-for-talking-with-your-health-care-provider" target="_blank" rel="noreferrer">NIMH <ExternalLink size={14} aria-hidden="true" /></a>
        {" "}and
        {" "}<a href="https://www.nhs.uk/every-mind-matters/supporting-others/helping-others/" target="_blank" rel="noreferrer">NHS Every Mind Matters <ExternalLink size={14} aria-hidden="true" /></a>.
      </p>
    </section>
  );
}

function BreathingTool({ hidden }) {
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(60);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseRemaining, setPhaseRemaining] = useState(breathingPhases[0].seconds);
  const timerRef = useRef(null);
  const phase = breathingPhases[phaseIndex];

  useEffect(() => {
    if (!running) return undefined;
    timerRef.current = window.setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          setRunning(false);
          return 0;
        }
        return value - 1;
      });
      setPhaseRemaining((value) => {
        if (value > 1) return value - 1;
        const next = (phaseIndex + 1) % breathingPhases.length;
        setPhaseIndex(next);
        return breathingPhases[next].seconds;
      });
    }, 1000);
    return () => window.clearInterval(timerRef.current);
  }, [running, phaseIndex]);

  useEffect(() => {
    if (hidden) setRunning(false);
  }, [hidden]);

  const reset = () => {
    setRunning(false);
    setRemaining(60);
    setPhaseIndex(0);
    setPhaseRemaining(breathingPhases[0].seconds);
  };

  return (
    <div className="breathing-tool" id="tool-breathe" role="tabpanel" aria-labelledby="tab-breathe" hidden={hidden}>
      <div className={`breathing-orb breathing-orb--${phaseIndex}`} aria-hidden="true">
        <span />
      </div>
      <div className="breathing-tool__copy">
        <p className="tool-kicker">One-minute reset</p>
        <h3>{remaining === 0 ? "You made a little room." : phase.name}</h3>
        <p className="breathing-tool__count" aria-live="polite">
          {remaining === 0 ? "Complete" : `${phaseRemaining} seconds · ${remaining} remaining`}
        </p>
        <p>Keep the breath comfortable. Stop if you feel light-headed or uncomfortable.</p>
        <div className="tool-actions">
          <button className="button button--ink" type="button" onClick={() => {
            if (remaining === 0) reset();
            setRunning((value) => !value);
          }}>
            {running ? <CirclePause size={18} aria-hidden="true" /> : <CirclePlay size={18} aria-hidden="true" />}
            {running ? "Pause" : remaining === 0 ? "Breathe again" : "Begin"}
          </button>
          <button className="button button--quiet" type="button" onClick={reset} disabled={remaining === 60}>
            <RotateCcw size={17} aria-hidden="true" /> Reset
          </button>
        </div>
      </div>
    </div>
  );
}

function GroundingTool({ hidden }) {
  const [step, setStep] = useState(0);
  const complete = step === groundingSteps.length;

  return (
    <div className="grounding-tool" id="tool-ground" role="tabpanel" aria-labelledby="tab-ground" hidden={hidden}>
      <div className="grounding-tool__progress" aria-label={`Step ${Math.min(step + 1, 5)} of 5`}>
        {groundingSteps.map((item, index) => (
          <span className={index < step ? "is-complete" : index === step ? "is-current" : ""} key={item} />
        ))}
      </div>
      <div className="grounding-tool__content">
        <p className="tool-kicker">5–4–3–2–1 grounding</p>
        <h3>{complete ? "You are here, in this moment." : groundingSteps[step]}</h3>
        <p>
          {complete
            ? "Notice whether anything shifted. There is no right result—finishing the exercise is enough."
            : "Take your time. You can name the things silently, out loud, or in a note beside you."}
        </p>
      </div>
      <div className="tool-actions">
        <button className="button button--quiet" type="button" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0}>
          <ChevronLeft size={18} aria-hidden="true" /> Back
        </button>
        <button className="button button--ink" type="button" onClick={() => setStep((value) => complete ? 0 : value + 1)}>
          {complete ? <RotateCcw size={18} aria-hidden="true" /> : <Check size={18} aria-hidden="true" />}
          {complete ? "Start again" : step === 4 ? "Finish" : "I noticed"}
          {!complete && step < 4 && <ChevronRight size={18} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

function MoodTool({ hidden }) {
  const [mood, setMood] = useState(3);
  const [note, setNote] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle");
  const labels = useMemo(() => ["Very difficult", "Difficult", "Mixed", "Steady", "Good"], []);

  const save = (event) => {
    event.preventDefault();
    const entry = { mood, note: note.trim(), createdAt: new Date().toISOString() };
    try {
      const existing = readLocalNotes();
      localStorage.setItem("neerja-mood-notes", JSON.stringify([entry, ...existing].slice(0, 14)));
      setSaveStatus("success");
      setNote("");
      window.setTimeout(() => setSaveStatus("idle"), 2500);
    } catch {
      setSaveStatus("error");
    }
  };

  return (
    <form className="mood-tool" id="tool-reflect" role="tabpanel" aria-labelledby="tab-reflect" hidden={hidden} onSubmit={save}>
      <div>
        <p className="tool-kicker">Private mood note</p>
        <h3>How does this moment feel?</h3>
        <p>Your note stays in this browser. It is not sent to Dr. Neerja or Firebase.</p>
      </div>
      <fieldset>
        <legend>Choose the closest description</legend>
        <div className="mood-scale">
          {labels.map((label, index) => (
            <label key={label}>
              <input type="radio" name="mood" value={index + 1} checked={mood === index + 1} onChange={() => setMood(index + 1)} />
              <span>{index + 1}</span>
              <small>{label}</small>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="field-label" htmlFor="mood-note">
        What feels most present? <span>Optional</span>
      </label>
      <textarea id="mood-note" value={note} onChange={(event) => { setNote(event.target.value); if (saveStatus === "error") setSaveStatus("idle"); }} placeholder="A thought, feeling, or situation…" maxLength={400} aria-invalid={saveStatus === "error"} />
      <div className="mood-tool__footer">
        <p aria-live="polite">{saveStatus === "success" ? "Saved privately on this device." : saveStatus === "error" ? "This browser blocked local storage. Copy the note somewhere private instead." : `${note.length}/400 characters`}</p>
        <button className="button button--ink" type="submit" data-state={saveStatus}>Save private note</button>
      </div>
    </form>
  );
}

function VisitPrepTool({ hidden }) {
  const [selected, setSelected] = useState([]);
  const [observations, setObservations] = useState("");
  const [questions, setQuestions] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [error, setError] = useState("");

  const toggleTopic = (id) => {
    setSelected((items) => items.includes(id) ? items.filter((item) => item !== id) : [...items, id]);
    setError("");
  };

  const noteText = useMemo(() => formatVisitNote(selected, observations, questions), [selected, observations, questions]);

  const buildNote = (event) => {
    event.preventDefault();
    if (!selected.length && !observations.trim() && !questions.trim()) {
      setError("The note is still empty. Choose a topic or add something you want to remember.");
      setShowNote(false);
      return;
    }
    setError("");
    setShowNote(true);
  };

  return (
    <form className="planning-tool planning-tool--visit" id="tool-prepare" role="tabpanel" aria-labelledby="tab-prepare" hidden={hidden} onSubmit={buildNote}>
      <div className="planning-tool__intro">
        <ClipboardList size={28} aria-hidden="true" />
        <div>
          <p className="tool-kicker">Before an appointment</p>
          <h3>Bring the parts you do not want to forget.</h3>
          <p>This note stays on this screen unless you choose to copy or download it.</p>
        </div>
      </div>

      <fieldset className="checklist-fieldset">
        <legend>What would be useful to mention?</legend>
        <div className="visit-checklist">
          {visitTopics.map((topic) => (
            <label key={topic.id}>
              <input type="checkbox" checked={selected.includes(topic.id)} onChange={() => toggleTopic(topic.id)} />
              <span><Check size={16} aria-hidden="true" /></span>
              {topic.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="planning-fields">
        <div>
          <label className="field-label" htmlFor="visit-observations">What have you noticed? <span>Optional</span></label>
          <textarea id="visit-observations" value={observations} onChange={(event) => { setObservations(event.target.value); setError(""); }} placeholder="For example: when it began, how often it happens, and what daily life feels like" maxLength={900} />
        </div>
        <div>
          <label className="field-label" htmlFor="visit-questions">Questions to ask <span>Optional</span></label>
          <textarea id="visit-questions" value={questions} onChange={(event) => { setQuestions(event.target.value); setError(""); }} placeholder="For example: What options should I understand? What should I monitor?" maxLength={600} />
        </div>
      </div>

      <p className={`form-message ${error ? "form-message--error" : ""}`} role={error ? "alert" : undefined}>{error || "You decide what to share. A companion can also help you take notes during the visit."}</p>
      <div className="tool-actions">
        <button className="button button--ink" type="submit"><ListTodo size={17} aria-hidden="true" /> Build my note</button>
      </div>

      {showNote && (
        <section className="generated-note" aria-labelledby="visit-note-title">
          <div>
            <p className="tool-kicker">Ready to take with you</p>
            <h4 id="visit-note-title">My appointment note</h4>
          </div>
          <VisitNote selected={selected} observations={observations} questions={questions} />
          <div className="tool-actions">
            <CopyButton text={noteText} label="Copy note" />
            <button className="button button--quiet" type="button" onClick={() => downloadText("appointment-note.txt", noteText)}><Download size={17} aria-hidden="true" /> Download</button>
          </div>
        </section>
      )}
    </form>
  );
}

function VisitNote({ selected, observations, questions }) {
  const topics = visitTopics.filter((topic) => selected.includes(topic.id));
  return (
    <div className="generated-note__body">
      {!!topics.length && <div><strong>Topics I want to cover</strong><ul>{topics.map((topic) => <li key={topic.id}>{topic.label}</li>)}</ul></div>}
      {observations.trim() && <div><strong>What I have noticed</strong><p>{observations.trim()}</p></div>}
      {questions.trim() && <div><strong>Questions I want to ask</strong><p>{questions.trim()}</p></div>}
    </div>
  );
}

function SupportWordsTool({ hidden }) {
  const [selectedId, setSelectedId] = useState(supportScripts[0].id);
  const selected = supportScripts.find((item) => item.id === selectedId) || supportScripts[0];

  return (
    <div className="planning-tool support-words-tool" id="tool-support" role="tabpanel" aria-labelledby="tab-support" hidden={hidden}>
      <div className="planning-tool__intro">
        <HeartHandshake size={28} aria-hidden="true" />
        <div>
          <p className="tool-kicker">For families and friends</p>
          <h3>Start with care, then make room to listen.</h3>
          <p>Choose the moment you are preparing for. Use the words as a starting point, not a script you must follow exactly.</p>
        </div>
      </div>

      <fieldset className="script-picker">
        <legend>What do you want to say?</legend>
        <div>
          {supportScripts.map((item) => (
            <label key={item.id}>
              <input type="radio" name="support-script" value={item.id} checked={selectedId === item.id} onChange={() => setSelectedId(item.id)} />
              <span>
                <strong>{item.title}</strong>
                <small>{item.note}</small>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <section className={`conversation-note ${selected.urgent ? "conversation-note--urgent" : ""}`} aria-live="polite">
        {selected.urgent && <AlertTriangle size={22} aria-hidden="true" />}
        <blockquote>“{selected.script}”</blockquote>
        <CopyButton text={selected.script} label="Copy words" />
        {selected.urgent && (
          <div className="urgent-links">
            <a className="button button--ink" href="tel:112">Call 112</a>
            <a className="button button--quiet" href="tel:14416">Call 14416</a>
          </div>
        )}
      </section>
    </div>
  );
}

function ThoughtSorterTool({ hidden }) {
  const [thought, setThought] = useState("");
  const [control, setControl] = useState("unsure");
  const [nextStep, setNextStep] = useState("");
  const [errors, setErrors] = useState({ thought: false, nextStep: false });
  const [showResult, setShowResult] = useState(false);

  const resultText = useMemo(() => formatThoughtNote(thought, control, nextStep), [thought, control, nextStep]);

  const sortThought = (event) => {
    event.preventDefault();
    const nextErrors = { thought: !thought.trim(), nextStep: !nextStep.trim() };
    setErrors(nextErrors);
    setShowResult(!nextErrors.thought && !nextErrors.nextStep);
  };

  return (
    <form className="planning-tool thought-sorter" id="tool-sort" role="tabpanel" aria-labelledby="tab-sort" hidden={hidden} onSubmit={sortThought}>
      <div className="planning-tool__intro">
        <ListTodo size={28} aria-hidden="true" />
        <div>
          <p className="tool-kicker">A two-minute thought sorter</p>
          <h3>Give the worry a boundary.</h3>
          <p>This does not solve the whole situation. It helps you name one thought and one next step without saving either.</p>
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor="thought-text">What is taking up space right now?</label>
        <textarea id="thought-text" value={thought} onChange={(event) => { setThought(event.target.value); if (errors.thought) setErrors((value) => ({ ...value, thought: false })); }} placeholder="Write one concern in a sentence or two" maxLength={500} aria-invalid={errors.thought} aria-describedby="thought-help" />
        <p id="thought-help" className={`field-help ${errors.thought ? "field-help--error" : ""}`}>{errors.thought ? "The thought is missing. Name one concern before sorting it." : `${thought.length}/500 characters`}</p>
      </div>

      <fieldset className="control-picker">
        <legend>How much of this can you influence today?</legend>
        <div>
          {["Some of it", "Not much", "I’m unsure"].map((label) => {
            const value = label === "Some of it" ? "some" : label === "Not much" ? "outside" : "unsure";
            return <label key={value}><input type="radio" name="control" value={value} checked={control === value} onChange={() => setControl(value)} /><span>{label}</span></label>;
          })}
        </div>
      </fieldset>

      <div>
        <label className="field-label" htmlFor="next-step">What is one kind, practical next step?</label>
        <input id="next-step" value={nextStep} onChange={(event) => { setNextStep(event.target.value); if (errors.nextStep) setErrors((value) => ({ ...value, nextStep: false })); }} placeholder="For example: send one message, pause, or write down a question" maxLength={180} aria-invalid={errors.nextStep} aria-describedby="step-help" />
        <p id="step-help" className={`field-help ${errors.nextStep ? "field-help--error" : ""}`}>{errors.nextStep ? "The next step is missing. Choose something small enough to do or schedule." : "Keep it smaller than the whole problem."}</p>
      </div>

      <div className="tool-actions">
        <button className="button button--ink" type="submit">Sort this thought</button>
      </div>

      {showResult && (
        <section className="generated-note" aria-labelledby="thought-result-title">
          <div>
            <p className="tool-kicker">For this moment</p>
            <h4 id="thought-result-title">A smaller frame</h4>
          </div>
          <div className="generated-note__body">
            <div><strong>What is taking up space</strong><p>{thought.trim()}</p></div>
            <div><strong>What I know right now</strong><p>{controlSentence(control)}</p></div>
            <div><strong>My next step</strong><p>{nextStep.trim()}</p></div>
          </div>
          <CopyButton text={resultText} label="Copy reflection" />
        </section>
      )}
    </form>
  );
}

function CopyButton({ text, label }) {
  const [state, setState] = useState("idle");

  const copy = async () => {
    setState("loading");
    try {
      await copyToClipboard(text);
      setState("success");
      window.setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("error");
      window.setTimeout(() => setState("idle"), 3000);
    }
  };

  const buttonLabel = state === "loading" ? "Copying…" : state === "success" ? "Copied" : state === "error" ? "Copy failed" : label;
  return (
    <button className="button button--quiet copy-button" type="button" onClick={copy} disabled={state === "loading"} data-state={state} aria-live="polite">
      {state === "success" ? <Check size={17} aria-hidden="true" /> : <Copy size={17} aria-hidden="true" />}
      {buttonLabel}
    </button>
  );
}

function readLocalNotes() {
  try {
    const value = JSON.parse(localStorage.getItem("neerja-mood-notes") || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function formatVisitNote(selected, observations, questions) {
  const topics = visitTopics.filter((topic) => selected.includes(topic.id)).map((topic) => `- ${topic.label}`);
  return [
    "MY APPOINTMENT NOTE",
    topics.length ? `\nTopics I want to cover\n${topics.join("\n")}` : "",
    observations.trim() ? `\nWhat I have noticed\n${observations.trim()}` : "",
    questions.trim() ? `\nQuestions I want to ask\n${questions.trim()}` : "",
  ].filter(Boolean).join("\n");
}

function controlSentence(control) {
  if (control === "some") return "There is at least one part I may be able to influence today.";
  if (control === "outside") return "Much of this may be outside my control today. I can still choose how I care for myself around it.";
  return "I do not need to decide how controllable everything is before taking one useful step.";
}

function formatThoughtNote(thought, control, nextStep) {
  return `A SMALLER FRAME\n\nWhat is taking up space\n${thought.trim()}\n\nWhat I know right now\n${controlSentence(control)}\n\nMy next step\n${nextStep.trim()}`;
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  const copied = document.execCommand("copy");
  field.remove();
  if (!copied) throw new Error("Copy failed");
}

function downloadText(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
